import React, { useState } from "react";

/**
 * Avatar — privacy-safe, never-broken avatar renderer.
 *
 * Renders a glowing cyan-gradient circle with the user's initial when:
 *   - no avatar value is provided
 *   - the value is a slug (e.g. "neon-orbit") rather than an http(s) URL
 *   - the <img> fails to load (network/404)
 *
 * Otherwise renders the image and clips it to a circle.
 *
 * Props:
 *   src       (string|null)  - avatar_url from profiles
 *   name      (string|null)  - username to derive initial + a11y alt text
 *   size      (number)       - pixel size (default 36)
 *   className (string)       - extra container classes
 *   testId    (string)       - data-testid passthrough
 */
export default function Avatar({ src, name, size = 36, className = "", testId }) {
  const [broken, setBroken] = useState(false);

  const initial = (name || "P").trim().slice(0, 1).toUpperCase() || "P";
  const isHttp = typeof src === "string" && /^https?:\/\//i.test(src);
  const showImg = isHttp && !broken;

  const dim = { width: size, height: size, fontSize: Math.max(11, Math.floor(size * 0.42)) };

  const baseCls =
    "relative inline-flex items-center justify-center rounded-full overflow-hidden " +
    "bg-gradient-to-br from-cyan-400 via-cyan-500 to-sky-700 text-black font-black " +
    "shadow-[0_0_16px_rgba(34,211,238,0.35)] ring-1 ring-cyan-300/40 select-none";

  return (
    <span
      className={`${baseCls} ${className}`}
      style={dim}
      data-testid={testId}
      aria-label={name || "Pilot"}
    >
      {showImg ? (
        <img
          src={src}
          alt={name || "Pilot"}
          onError={() => setBroken(true)}
          className="w-full h-full object-cover"
          draggable={false}
        />
      ) : (
        <span aria-hidden="true">{initial}</span>
      )}
    </span>
  );
}
